import { Button } from '@/components/Button';
import { Chip } from '@/components/Chip';
import { ENROLLMENT_STATUS } from '@/constants';
import { useLocale } from '@/providers/LocaleProvider';
import IconOpenedBook from '@/resources/images/icon-opened-book.svg';
import ChevronRightIcon from '@/resources/images/icons-chevron-right.svg';
import { KLAYO_COLORS } from '@/themes';
import { CourseFaculty } from '@/types/views/courses.types';
import { getEnrollmentStatusColor } from '@/utilities/enrollmentCourseStatusColor';
import CircleIcon from '@mui/icons-material/Circle';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, capitalize, Collapse, IconButton, Stack, Typography } from '@mui/material';
import { lowerCase } from 'lodash';
import { MouseEvent, SyntheticEvent } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { useNavigate } from 'react-router';

/** Minimal course shape the accordion renders — works for both career learning pathways and
 *  learning-path course components. */
export interface LearningPathAccordionCourse {
  id: string;
  title: string;
  imageUrl?: string | null;
  numberOfModules?: number;
  faculties?: CourseFaculty[];
  descriptionHtml?: string;
  enrollmentStatus?: string | null;
  /** Past due and not passed. Rendered as its own marker beside the status chip — the API used to
   *  fold it INTO enrollmentStatus, which sent getActionLabel to its default ("Start") and left the
   *  chip grey. */
  isOverdue?: boolean;
  enrollmentId?: string | null;
  enrollmentInstance?: number | null;
  canLaunch: boolean;
  /** Render status-only (no launch action) — used for a diploma's component learning paths,
   *  which aren't directly launchable. */
  statusOnly?: boolean;
}

interface LearningPathCourseAccordionProps {
  course: LearningPathAccordionCourse;
  index: number;
  /** Render as part of a connected list (no own radius; a top divider separates items) instead
   *  of a standalone rounded card. The parent supplies the outer border/radius. */
  grouped?: boolean;
  /** Controlled expansion — the parent keeps only one course open at a time (mirrors the
   *  course-modules accordion). */
  expanded: boolean;
  onChange: (event: SyntheticEvent, isExpanded: boolean) => void;
  isTrainingManager?: boolean;
  /** Fired when the user clicks "Go to course" (navigates to the training's detail page) — used
   *  for career-path analytics. */
  onGoToCourse?: (course: LearningPathAccordionCourse) => void;
}

export const LearningPathCourseAccordion = ({
  course,
  index,
  grouped = false,
  expanded,
  onChange,
  isTrainingManager,
  onGoToCourse,
}: LearningPathCourseAccordionProps) => {
  const { translate } = useLocale();
  const navigate = useNavigate();

  const getActionLabel = (status: string) => {
    switch (status) {
      case ENROLLMENT_STATUS.IN_PROGRESS:
      case ENROLLMENT_STATUS.PENDING_REVIEW:
        return 'common.resume';
      case ENROLLMENT_STATUS.FAILED:
        return 'common.retake';
      case ENROLLMENT_STATUS.COMPLETED:
      case ENROLLMENT_STATUS.PASSED:
        return 'common.relaunch';
      default:
        return 'common.start';
    }
  };

  const handleGoToCourse = (courseId: string) => {
    onGoToCourse?.(course);
    if (isTrainingManager) {
      navigate(`/settings/learning/${courseId}`);
    } else {
      navigate(`/my-klayo/courses/${courseId}`);
    }
  };

  return (
    <Box
      sx={{
        overflow: 'hidden',
        ...(grouped
          ? { borderTop: index > 0 ? `1px solid ${KLAYO_COLORS.GreyGR200}` : 'none' }
          : {
              border: `1px solid ${KLAYO_COLORS.GreyGR200}`,
              borderRadius: '16px',
            }),
      }}
    >
      {/* Summary row */}
      <Stack
        direction='row'
        alignItems='center'
        gap={2}
        px={'16px'}
        py={'20px'}
        sx={{ cursor: 'pointer' }}
        onClick={(e) => onChange(e, !expanded)}
      >
        {/* Thumbnail */}
        <Box
          sx={{
            width: 88,
            height: 88,
            borderRadius: '6px',
            flexShrink: 0,
            overflow: 'hidden',
            background: `linear-gradient(360deg, ${KLAYO_COLORS.GreyGR200} 0%, ${KLAYO_COLORS.WhiteWH900} 100%)`,
            display: {
              xs: 'none',
              md: 'block',
            },
            position: 'relative',
            border: `1px solid ${KLAYO_COLORS.NeonBlueNB050}`,
          }}
        >
          {course.imageUrl ? (
            <LazyLoadImage
              className='klayo_card_image'
              src={course.imageUrl}
              alt={course.title}
              effect='blur'
              width={'100%'}
              style={{ aspectRatio: '1/1' }}
            />
          ) : (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <IconOpenedBook width={24} height={24} color={KLAYO_COLORS.GreyGR200} />
            </Box>
          )}
        </Box>

        {/* Title + meta + chips */}
        <Stack gap={1} flex={1} minWidth={0}>
          <Typography
            variant='h6'
            fontWeight={600}
            fontSize={{
              xs: 16,
              md: 18,
            }}
          >
            {course.title}
          </Typography>
          <Typography variant='body2' color={KLAYO_COLORS.GreyGR500} component='div'>
            {[
              { text: translate('myKlayo.courseNumber', { number: index + 1 }), noWrap: true },
              course.numberOfModules
                ? {
                    text: translate('coursesPage.modules', { count: course.numberOfModules }),
                    noWrap: true,
                  }
                : null,
              course.faculties?.length
                ? { text: course.faculties.map((f) => f.name).join(', '), noWrap: false }
                : null,
            ]
              .filter((item): item is { text: string; noWrap: boolean } => Boolean(item))
              .map((item, i) => (
                <Box
                  component='span'
                  key={i}
                  sx={{ whiteSpace: item.noWrap ? 'nowrap' : 'normal' }}
                >
                  {i > 0 && (
                    <CircleIcon sx={{ width: 6, height: 6, mx: 1.5, verticalAlign: 'middle' }} />
                  )}
                  {item.text}
                </Box>
              ))}
          </Typography>

          {!!course.enrollmentStatus && (
            <Stack direction='row' alignItems='center' gap={1} flexWrap='wrap'>
              <Chip
                backgroundColor={getEnrollmentStatusColor(course.enrollmentStatus || '')}
                textColor={KLAYO_COLORS.WhiteWH900}
                label={capitalize(lowerCase(course.enrollmentStatus || ''))}
              />
              {course.isOverdue && (
                <Typography variant='body2' color={KLAYO_COLORS.TouchRedTR500}>
                  {translate('manage.overdue')}
                </Typography>
              )}
              {!course.statusOnly && (
                <Button
                  label={translate(getActionLabel(course.enrollmentStatus || ''))}
                  variant='text'
                  size='sm'
                  onClick={(e: MouseEvent) => {
                    e.stopPropagation();
                    navigate(
                      `/my-klayo/enrollments/${course?.enrollmentId}?instance=${course?.enrollmentInstance ?? 1}`,
                      { state: { from: location.pathname } }
                    );
                  }}
                  sx={{
                    height: '24px',
                    padding: '2px 12px',
                  }}
                  disabled={!course.canLaunch}
                />
              )}
            </Stack>
          )}
        </Stack>

        {/* Chevron */}
        <IconButton size='small' sx={{ flexShrink: 0, mt: 0.25 }}>
          {expanded ? <ExpandLessIcon fontSize='small' /> : <ExpandMoreIcon fontSize='small' />}
        </IconButton>
      </Stack>

      {/* Expandable details */}
      <Collapse in={expanded}>
        <Stack gap={1.5} px={2} pb={2}>
          {course.descriptionHtml && (
            <Typography
              variant='body2'
              color={KLAYO_COLORS.GreyGR700}
              dangerouslySetInnerHTML={{ __html: course.descriptionHtml }}
            />
          )}
          <Stack width={'max-content'}>
            <Button
              variant='text'
              onClick={() => handleGoToCourse(course?.id)}
              label={translate('common.goToCourse')}
              endIcon={<ChevronRightIcon />}
              size='sm'
            />
          </Stack>
        </Stack>
      </Collapse>
    </Box>
  );
};
